'use client';

import { useEffect, useRef, useState } from 'react';
import { Camera, Image as ImageIcon, Loader2, Trash2 } from 'lucide-react';
import type { Person } from '@/lib/types';
import { useProfile } from './ProfileContext';
import { Avatar } from './Avatar';

/**
 * An avatar that is also the photo control: tap it for Take photo / Choose
 * photo / Remove photo in a liquid-glass menu (the platform's
 * UploadableAvatar, without Radix). The new picture shows the moment it is
 * picked, from an object URL, and is swapped for MP's file URL when the
 * upload lands; a failure puts the old one back.
 */
export function PhotoAvatar({
  person,
  size = 96,
  onChange,
}: {
  person: Pick<Person, 'contactId' | 'displayName' | 'imageUrl'>;
  size?: number;
  /** The new image URL (null after a remove) */
  onChange: (imageUrl: string | null) => void;
}) {
  const { api, run } = useProfile();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [canTakePhoto, setCanTakePhoto] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCanTakePhoto(window.matchMedia?.('(pointer: coarse)')?.matches ?? false);
  }, []);

  // Close on outside click (composedPath so it works from inside the shadow root)
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      const target = e.composedPath()[0] as Node;
      if (wrapRef.current && !wrapRef.current.contains(target)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    setOpen(false);
    if (!file) return;
    const previous = person.imageUrl;
    const preview = URL.createObjectURL(file);
    onChange(preview);
    setBusy(true);
    await run(
      async () => {
        const { imageUrl } = await api.uploadPhoto(person.contactId, file);
        onChange(imageUrl || preview);
      },
      () => onChange(previous),
      'Photo updated'
    );
    setBusy(false);
  };

  const handleRemove = async () => {
    setOpen(false);
    const previous = person.imageUrl;
    onChange(null);
    setBusy(true);
    await run(
      () => api.removePhoto(person.contactId),
      () => onChange(previous),
      'Photo removed'
    );
    setBusy(false);
  };

  const item =
    'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-semibold transition-colors hover:bg-gray-50 active:bg-gray-100';

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        aria-label="Change photo"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        onClick={() => setOpen((o) => !o)}
        className="group relative rounded-full transition-transform hover:scale-[1.03] active:scale-[0.98] disabled:opacity-80"
      >
        <Avatar name={person.displayName} imageUrl={person.imageUrl} size={size} />
        <span
          className="absolute -right-0.5 -bottom-0.5 flex items-center justify-center rounded-full bg-white text-[#1c2b39] shadow-md ring-1 ring-gray-200"
          style={{ width: Math.round(size * 0.32), height: Math.round(size * 0.32) }}
        >
          {busy ? (
            <Loader2 className="h-[55%] w-[55%] animate-spin" strokeWidth={2.5} />
          ) : (
            <Camera className="h-[55%] w-[55%]" strokeWidth={2.5} />
          )}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute top-full left-0 z-[60] mt-2 w-48 overflow-hidden rounded-2xl bg-white/90 py-1 text-[#1c2b39] shadow-xl ring-1 ring-gray-200 backdrop-blur"
        >
          {canTakePhoto && (
            <button
              type="button"
              role="menuitem"
              className={item}
              onClick={() => cameraRef.current?.click()}
            >
              <Camera className="h-4 w-4 text-gray-500" strokeWidth={2.5} />
              Take photo
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => fileRef.current?.click()}
          >
            <ImageIcon className="h-4 w-4 text-gray-500" strokeWidth={2.5} />
            Choose photo
          </button>
          {person.imageUrl && (
            <button
              type="button"
              role="menuitem"
              className={`${item} text-red-600`}
              onClick={handleRemove}
            >
              <Trash2 className="h-4 w-4" strokeWidth={2.5} />
              Remove photo
            </button>
          )}
        </div>
      )}

      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={handleFile}
      />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
}
